import { useEffect, useMemo, useState } from 'react';
import {
  useStartConsolidation,
  useConsolidationRun,
  useApplyConsolidation,
  useRejectConsolidation,
} from '../../hooks/useSemanticGaps';
import type {
  ConsolidationRun,
  OriginalGapSnapshot,
  SemanticGapPriority,
  SemanticGapType,
} from '../../services/semanticGap.service';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Loader2,
  Merge,
  Trash2,
  ArrowRight,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';

const GAP_TYPE_LABELS: Record<SemanticGapType, string> = {
  missing_metric: 'Missing Metric',
  missing_dimension: 'Missing Dimension',
  missing_relationship: 'Missing Relationship',
  missing_source: 'Missing Source',
  data_quality: 'Data Quality',
  ambiguous_naming: 'Ambiguous Naming',
};

const PRIORITY_LABELS: Record<SemanticGapPriority, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

function PriorityBadge({ priority }: { priority: SemanticGapPriority }) {
  const variant = {
    high: 'destructive' as const,
    medium: 'warning' as const,
    low: 'secondary' as const,
  }[priority];
  return <Badge variant={variant}>{PRIORITY_LABELS[priority]}</Badge>;
}

function ProposedGapCard({
  gap,
  origById,
}: {
  gap: ConsolidationRun['proposed_gaps'][number];
  origById: Record<string, OriginalGapSnapshot>;
}) {
  const sources = gap.source_gap_ids
    .map((id) => origById[id])
    .filter(Boolean) as OriginalGapSnapshot[];
  const isMerge = sources.length > 1;
  const single = sources.length === 1 ? sources[0] : null;
  const reprioritized = single && single.priority !== gap.priority;

  return (
    <div className="border rounded-md p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-sm truncate" title={gap.title}>
            {gap.title}
          </div>
          <div className="text-xs text-muted-foreground">
            {GAP_TYPE_LABELS[gap.gap_type] || gap.gap_type}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {reprioritized && single && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <PriorityBadge priority={single.priority} />
              <ArrowRight className="h-3 w-3" />
            </span>
          )}
          <PriorityBadge priority={gap.priority} />
        </div>
      </div>

      {isMerge && (
        <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Merge className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Merged from {sources.length}:{' '}
            {sources.map((s) => s.title).join(' · ')}
          </span>
        </div>
      )}
      {!isMerge && single && (
        <div className="text-xs text-muted-foreground">
          {reprioritized ? 'Reprioritized' : 'Unchanged'} · from “{single.title}”
        </div>
      )}

      {gap.rationale && (
        <p className="text-xs text-muted-foreground italic">{gap.rationale}</p>
      )}
    </div>
  );
}

export function ConsolidateGapsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [runId, setRunId] = useState<string | null>(null);

  const startMutation = useStartConsolidation();
  const { data: run } = useConsolidationRun(runId);
  const applyMutation = useApplyConsolidation();
  const rejectMutation = useRejectConsolidation();

  // On open, reuse the cached proposal if there is one (force = false).
  useEffect(() => {
    if (open && !runId && !startMutation.isPending) {
      startMutation.mutate(false, {
        onSuccess: (created) => setRunId(created.id),
      });
    }
    if (!open) {
      setRunId(null);
      startMutation.reset();
      applyMutation.reset();
      rejectMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const origById = useMemo(() => {
    const map: Record<string, OriginalGapSnapshot> = {};
    (run?.original_gaps || []).forEach((g) => {
      map[g.gap_id] = g;
    });
    return map;
  }, [run?.original_gaps]);

  const droppedById = useMemo(
    () => (run?.dropped_gaps || []).map((d) => ({ ...d, gap: origById[d.gap_id] })),
    [run?.dropped_gaps, origById]
  );

  const status = run?.status;
  const isWorking =
    startMutation.isPending || !run || status === 'pending';
  const isReady = status === 'ready';
  const isApplied = status === 'applied';

  const counts = useMemo(() => {
    const proposed = run?.proposed_gaps || [];
    const before = run?.original_gaps.length || 0;
    const after = proposed.length;
    // Partition the original gaps by what happened to them so the numbers
    // sum to `before` and don't double-count (merged + kept + dropped).
    const merged = proposed
      .filter((p) => p.source_gap_ids.length > 1)
      .reduce((sum, p) => sum + p.source_gap_ids.length, 0);
    const kept = proposed.filter((p) => p.source_gap_ids.length === 1).length;
    const dropped = run?.dropped_gaps.length || 0;
    return { before, after, merged, kept, dropped };
  }, [run]);

  const breakdown = useMemo(
    () =>
      [
        counts.merged > 0 && `${counts.merged} merged`,
        counts.kept > 0 && `${counts.kept} kept as-is`,
        counts.dropped > 0 && `${counts.dropped} dropped`,
      ]
        .filter(Boolean)
        .join(' · '),
    [counts]
  );

  const handleApply = () => {
    if (!runId) return;
    applyMutation.mutate(runId, {
      onSuccess: () => onOpenChange(false),
    });
  };

  // Force a fresh run, bypassing the 24h cache.
  const handleRecycle = () => {
    setRunId(null);
    startMutation.mutate(true, {
      onSuccess: (created) => setRunId(created.id),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Consolidate Semantic Gaps</DialogTitle>
          <DialogDescription>
            Ellie reviews the open gaps to remove duplicates, merge related
            ones, and reprioritize against the current semantic layer. Nothing
            changes until you apply.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {isWorking && (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Analyzing open gaps…
              </p>
            </div>
          )}

          {status === 'failed' && (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
              <div>
                <p className="text-destructive text-sm font-medium">
                  Consolidation failed
                </p>
                <p className="text-destructive text-xs mt-1">{run?.error}</p>
              </div>
            </div>
          )}

          {isReady && counts.before === 0 && (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No open gaps to consolidate.
            </p>
          )}

          {isReady && counts.before > 0 && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant="secondary">{counts.before} open</Badge>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <Badge variant="default">{counts.after} consolidated</Badge>
                </div>
                {breakdown && (
                  <p className="text-xs text-muted-foreground">{breakdown}</p>
                )}
              </div>

              <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-1">
                {run?.proposed_gaps.map((p) => (
                  <ProposedGapCard key={p.temp_id} gap={p} origById={origById} />
                ))}

                {droppedById.length > 0 && (
                  <div className="space-y-2 pt-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Trash2 className="h-4 w-4" />
                      Dropped ({droppedById.length})
                    </div>
                    {droppedById.map((d) => (
                      <div
                        key={d.gap_id}
                        className="border border-dashed rounded-md p-2.5 text-xs"
                      >
                        <span className="line-through text-muted-foreground">
                          {d.gap?.title || d.gap_id}
                        </span>
                        {d.rationale && (
                          <p className="text-muted-foreground italic mt-1">
                            {d.rationale}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          {(isReady || status === 'failed') && (
            <Button
              variant="ghost"
              className="sm:mr-auto"
              onClick={handleRecycle}
              disabled={startMutation.isPending || applyMutation.isPending}
              title="Discard this proposal and run a fresh consolidation"
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${
                  startMutation.isPending ? 'animate-spin' : ''
                }`}
              />
              Recycle
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={applyMutation.isPending}
          >
            Cancel
          </Button>
          {isReady && counts.before > 0 && (
            <>
              <Button
                variant="outline"
                onClick={() =>
                  runId &&
                  rejectMutation.mutate(runId, {
                    onSuccess: () => onOpenChange(false),
                  })
                }
                disabled={applyMutation.isPending || rejectMutation.isPending}
              >
                Reject
              </Button>
              <Button onClick={handleApply} disabled={applyMutation.isPending}>
                {applyMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Apply Consolidation
              </Button>
            </>
          )}
          {isApplied && (
            <Badge variant="success" className="self-center">
              Applied
            </Badge>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
